'use client';
import { useEffect, useState, useContext, useRef } from 'react';
import { useWatchlistContext } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { numberShorthand } from '@benzinga/utils';
import type { WatchlistRelatedSymbol } from '@benzinga/scanner-manager';

import { TickerLogo } from '../../../edge/dashboard/_components/TickerLogo';
import { useTickerLogos } from '../../../edge/dashboard/_components/useTickerLogos';
import { getWatchlistPeers } from '../data';
import { WatchlistNewsFeed } from './newsfeed/WatchlistNewsFeed';
import { getSectorColor } from './utils';
import { PeersGridSkeleton } from './WatchlistInsightsSkeleton';

export const WatchlistPeers = () => {
  const { selectedWatchlist } = useWatchlistContext();
  const session = useContext(SessionContext);
  const [peers, setPeers] = useState<WatchlistRelatedSymbol[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const selectedWatchlistRef = useRef(selectedWatchlist);
  selectedWatchlistRef.current = selectedWatchlist;
  const watchlistId = selectedWatchlist?.watchlistId;

  useEffect(() => {
    if (!watchlistId) {
      setPeers([]);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const fetchPeers = async () => {
      setIsLoading(true);
      try {
        const symbols = selectedWatchlistRef.current?.symbols.map(symbol => symbol.symbol) ?? [];
        const nextPeers = await getWatchlistPeers(symbols, session.getSession());

        if (abortController.signal.aborted) return;
        setPeers(nextPeers);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Error fetching peers', error);
        if (!abortController.signal.aborted) setPeers([]);
      } finally {
        if (!abortController.signal.aborted) setIsLoading(false);
      }
    };

    fetchPeers();

    return () => {
      abortController.abort();
    };
  }, [session, watchlistId]);

  const logoBySymbol = useTickerLogos(peers.map(peer => peer.symbol));

  const badge = {
    title: 'Peers',
    description:
      'Have a specific sector or business you believe in? Follow others in the space to get a good indication of how your tickers may move. This will give you additional reference points to help you make informed decisions.',
    button: 'Unlock Peer Searching',
  };

  return (
    <WatchlistNewsFeed
      title="Suggested Peers"
      subtitle="Related tickers in similar sectors"
      badge={badge}
      isLoading={isLoading}
      newsCount={peers.length}
      skeleton={<PeersGridSkeleton />}
      emptyMinHeightClass="min-h-[692px] sm:min-h-[340px] lg:min-h-[276px] xl:min-h-[164px]"
      emptyState={{
        title: 'No suggested peers right now',
        description: 'Add more tickers to your watchlist to discover related companies in similar sectors.',
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {peers.map(peer => (
          <a
            href={`https://www.benzinga.com/quote/${peer.symbol}`}
            target="_blank"
            rel="noopener noreferrer"
            key={peer.symbol}
            className="group flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3.5 no-underline transition-[border-color,background-color] duration-150 ease-out hover:border-bzblue-400 hover:bg-bzblue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bzblue-700"
          >
            <div className="flex min-w-0 items-start gap-2">
              <TickerLogo size={20} src={logoBySymbol[peer.symbol]} symbol={peer.symbol} />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-950 transition-colors duration-150 group-hover:text-bzblue-800">
                  {peer.name}
                </p>
                <p className="mt-0.5 text-xs font-semibold text-bzblue-800 transition-colors duration-150 group-hover:text-bzblue-700">
                  {peer.symbol}
                </p>
                {peer.sector && (
                  <span
                    className={`mt-2 inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${getSectorColor(peer.sector)}`}
                  >
                    {peer.sector}
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-sm font-bold tabular-nums text-slate-900">${numberShorthand(peer.marketCap)}</p>
              <p className="text-[10px] font-medium text-slate-400">Market cap</p>
            </div>
          </a>
        ))}
      </div>
    </WatchlistNewsFeed>
  );
};
